import React from 'react';
import { motion, Variants } from 'framer-motion';

export interface VoloceanLoaderProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  message?: string | null;
  className?: string;
}

/**
 * Ultra-Minimalist Premium Global Loader
 * Subtle breathing pulse pattern
 */
const VoloceanLoader: React.FC<VoloceanLoaderProps> = ({
  size = 'md',
  message = 'Loading...',
  className = ''
}) => {
  // Drastically reduced sizes for a more refined, engineered look
  const dotSizeClasses = {
    sm: 'w-1 h-1',
    md: 'w-1.5 h-1.5',
    lg: 'w-2 h-2',
    xl: 'w-2.5 h-2.5'
  };

  // Container variants for the stagger effect
  const containerVariants: Variants = {
    animate: {
      transition: {
        staggerChildren: 0.2, // Slightly slower stagger for a calmer breath
      },
    },
  };

  // Dot variants for the breathing pulse effect (no vertical jumping)
  const dotVariants: Variants = {
    initial: { opacity: 0.2, scale: 0.8 },
    animate: {
      opacity: [0.2, 1, 0.2],
      scale: [0.8, 1, 0.8],
      transition: {
        duration: 1.5, // Longer duration = calmer rhythm
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  return (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      {/* Minimalist Breathing Pulse Container */}
      <motion.div
        className="flex gap-2.5"
        variants={containerVariants}
        initial="initial"
        animate="animate"
      >
        {[0, 1, 2, 3].map((i) => (
          <motion.div
            key={i}
            className={`${dotSizeClasses[size]} rounded-full bg-primary-500`}
            variants={dotVariants}
          />
        ))}
      </motion.div>

      {/* Subtle System Status Text (if provided) */}
      {message && (
        <motion.div
          className="mt-5 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <motion.p
            // Replaced loud gradient with subtle, technical uppercase text
            className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-neutral-400"
            animate={{
              opacity: [0.4, 0.7, 0.4]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            {message}
          </motion.p>
        </motion.div>
      )}
    </div>
  );
};

export default VoloceanLoader;
